import { SelectOption } from '@innowise-group/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import useDebounce from './use-debounce.hook';
import {
  LifecycleStatuses,
  SortingValue,
  allowedVacancyPriorityStatuses,
  allowedVacancyRequestStatuses,
} from '@constants';
import { RequestsService, VacanciesService } from '../services';
import { getArrayIds } from '@innowise-group/utilities';
import { useRecruiterCandidatesAPI } from '../store';

// A request 2.0 has no vacancy: unbound (`isBoundToVacancy = false`, the recruiter mode) the request does not follow
// the vacancy field and the options are searched among all requests
const useSearchVacancyRequest = (
  vacancyIdFieldName: string,
  vacancyNameFieldName: string,
  requestNameFieldName: string,
  vacancyIds?: string[],
  isBoundToVacancy = true,
) => {
  const [isBusyByFetchRequestOptionById, setBusyByFetchRequestOptionById] = useState(false);
  const [isBusyByHandleRequestChange, setBusyByHandleRequestChange] = useState(false);
  const [isBusyByFetchRequestsOptions, setBusyByFetchRequestsOptions] = useState(false);

  const isBusy = isBusyByFetchRequestOptionById || isBusyByFetchRequestsOptions || isBusyByHandleRequestChange;

  const { recruiterVacancyId } = useRecruiterCandidatesAPI();
  const [requestOptions, setRequestOptions] = useState<{ ids: number[]; items: SelectOption[] }>({
    ids: [],
    items: null,
  });
  const [requestValue, setRequestValue] = useState<string>('');
  const [debouncedRequestValue] = useDebounce<string>(requestValue, 500);

  const prevVacancyId = useRef(null);

  const { watch, setValue, trigger } = useFormContext();

  const currentVacancyId = watch(vacancyIdFieldName);
  const currentRequestId = watch(requestNameFieldName);

  const itemSet = useMemo(() => {
    return requestOptions.ids.reduce(
      (acc, id) => (requestOptions.items[id] ? [...acc, requestOptions.items[id]] : acc),
      [],
    );
  }, [requestOptions]);

  const transformValue = (value: string) => requestOptions.items?.[value]?.title;

  const fetchRequestOptionById = async (id: number) => {
    setBusyByFetchRequestOptionById(true);

    const { data } = await RequestsService.getVacancyRequestById(id).finally(() =>
      setBusyByFetchRequestOptionById(false),
    );

    setRequestOptions((prev) => ({
      ...prev,
      ids: [...new Set([...prev.ids, data.id])],
      items: {
        ...prev.items,
        [data.id]: {
          title: data.id.toString(),
          value: data.id.toString(),
        },
      },
    }));
  };

  const handleRequestChange = useCallback(
    (onChange: (value: string) => void) => async (value: string) => {
      onChange(value);
      // TODO(ITSRT2-1395): a request 2.0 has no vacancy yet, so there is nothing to fill in
      if (isBoundToVacancy && value && requestOptions.items?.[value]?.parent) {
        const parentVacancyId = requestOptions.items[value].parent;
        setBusyByHandleRequestChange(true);

        const response = await VacanciesService.getVacancyById(parentVacancyId).finally(() =>
          setBusyByHandleRequestChange(false),
        );

        if (response?.data) {
          setValue(vacancyIdFieldName, response.data.id.toString());
          setValue(vacancyNameFieldName, response.data.name);
          trigger(vacancyIdFieldName);
        }
      }
    },
    [requestOptions.items, vacancyIdFieldName, vacancyNameFieldName, isBoundToVacancy],
  );

  const handleSearchRequests = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRequestValue(event.target.value);
  }, []);

  const onCloseRequestSelect = useCallback(() => {
    setRequestValue('');
    if (!isBoundToVacancy) {
      fetchRequestsOptions('', '');
    } else if (!currentVacancyId) {
      if (!(recruiterVacancyId && prevVacancyId.current === null)) {
        fetchRequestsOptions('', '');
      }
    } else {
      fetchRequestsOptions('', currentVacancyId);
    }
  }, [currentVacancyId, recruiterVacancyId, isBoundToVacancy]);

  const fetchRequestsOptions = useCallback(
    async (searchValue: string, vacancyId?: string) => {
      // TODO(ITSRT2-1395): the request 2.0 search ignores the status and the vacancy until the backend reworks them
      const body = {
        searchBarRequest: searchValue,
        lifecycleStatus: LifecycleStatuses.Actual,
        // TODO: Remove after updated dictionary request
        statuses: allowedVacancyRequestStatuses,
        priorities: allowedVacancyPriorityStatuses,
        ...(vacancyId ? { vacancyIds: [vacancyId] } : vacancyIds?.length ? { vacancyIds } : {}),
      };
      const params = { currentPage: 1, pageSize: 10, sort: SortingValue.IdASC };

      setBusyByFetchRequestsOptions(true);
      const response = await RequestsService.getAllRequests({ params, body }).finally(() =>
        setBusyByFetchRequestsOptions(false),
      );

      const ids = getArrayIds(response.data.content);

      setRequestOptions((prev) => ({
        ...prev,
        ids: [...ids, ...(!ids.includes(Number(currentRequestId)) ? [Number(currentRequestId)] : [])],
        items: {
          ...prev.items,
          ...response.data.content.reduce(
            (acc, { id, vacancy }) => ({
              ...acc,
              [id]: {
                value: id.toString(),
                title: id.toString(),
                parent: vacancy?.id,
              },
            }),
            {},
          ),
        },
      }));
    },
    [currentRequestId],
  );

  useEffect(() => {
    if (!currentRequestId) {
      onCloseRequestSelect();
    }
  }, [onCloseRequestSelect, currentRequestId]);

  useEffect(() => {
    if (!isBoundToVacancy) return;
    if (prevVacancyId.current && prevVacancyId.current.toString() !== currentVacancyId.toString()) {
      if (!recruiterVacancyId) {
        setValue(requestNameFieldName, '');
        fetchRequestsOptions('', currentVacancyId);
      }
    }
    if (!currentVacancyId) {
      setValue(requestNameFieldName, '');
    }
    prevVacancyId.current = currentVacancyId;
  }, [setValue, currentVacancyId, recruiterVacancyId, isBoundToVacancy]);

  useEffect(() => {
    if (!isBoundToVacancy) {
      if (debouncedRequestValue) fetchRequestsOptions(debouncedRequestValue, '');
      return;
    }
    if (debouncedRequestValue || currentVacancyId) {
      fetchRequestsOptions(debouncedRequestValue || '', currentVacancyId);
    }
  }, [debouncedRequestValue, fetchRequestsOptions, currentVacancyId, isBoundToVacancy]);

  return {
    transformValue,
    handleRequestChange,
    onCloseRequestSelect,
    handleSearchRequests,
    fetchRequestOptionById,
    requestOptions: itemSet,
    isBusy,
  };
};

export default useSearchVacancyRequest;
